// P1.6 — GDPR consent state.
//
// Single source of truth for what the user has chosen and the only writer
// of the persisted record. The browser-side defaults already live in
// `index.html` (Consent Mode v2 `default 'denied'` for everything except
// `functionality_storage` / `security_storage`); this module just lets the
// React tree read the persisted choice, render the banner accordingly, and
// push updates into GTM via the `window.__lazysemConsent.update()` hook
// that the same `index.html` snippet exposes.
//
// Storage key is intentionally versioned: a future schema change (e.g. a
// new GTM category) bumps the version so old payloads are ignored and the
// banner re-prompts.

import { logger } from "./logger";

export const CONSENT_STORAGE_KEY = "lazysem_consent_v1";
export const OPEN_CONSENT_EVENT = "lazysem:open-consent";
export function openConsentPreferences(): void {
  window.dispatchEvent(new Event(OPEN_CONSENT_EVENT));
}

// Categories we surface in the UI. `functionality_storage` and
// `security_storage` are always granted (no UI toggle) because they cover
// session and CSRF cookies that the app cannot function without; declaring
// them explicitly keeps the persisted payload self-describing.
export type ConsentCategory =
  | "analytics_storage"
  | "ad_storage"
  | "ad_user_data"
  | "ad_personalization";

// Categories the banner actually exposes as toggles. The type keeps all four
// Consent Mode v2 signals; only this subset is user-controllable. Declared as
// a `const` tuple (not `ConsentCategory[]`) so `ToggleableCategory` below can
// be derived from it — that lets the banner's CATEGORY_LABELS lookup be typed
// as a total (non-Partial) Record over exactly this subset, so indexing it
// never needs a non-null assertion or an `as` cast.
export const TOGGLEABLE_CATEGORIES = ["analytics_storage"] as const;

export type ToggleableCategory = (typeof TOGGLEABLE_CATEGORIES)[number];

export type ConsentValue = "granted" | "denied";

export type ConsentChoice = Record<ConsentCategory, ConsentValue>;

export interface PersistedConsent extends ConsentChoice {
  // ISO-8601 timestamp of the user's most recent decision. Used by the audit
  // log and by future versions of the banner to re-prompt after N months
  // (GDPR best practice is 12 months).
  updated_at: string;
  // Schema version embedded in the payload so callers can defensively
  // ignore stale shapes even if the storage key didn't change.
  version: 1;
}

export const ALL_DENIED: ConsentChoice = {
  analytics_storage: "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
};

// "Accept all" grants analytics only. LazySEM runs no advertising or
// cross-site tracking tags, so the three ad_* Consent Mode v2 signals stay
// denied permanently — they remain in the payload because Consent Mode wants
// every signal declared, not because anything consumes them. If ad tech is
// ever added, it needs its own UI toggle, a Cookie Policy row, a subprocessor
// entry, and a CONSENT_STORAGE_KEY bump to force a re-prompt.
export const ACCEPT_ALL: ConsentChoice = {
  analytics_storage: "granted",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
};

interface ConsentBridge {
  update?: (partial: Partial<ConsentChoice>) => void;
  restoreFromStorage?: () => void;
}

function getBridge(): ConsentBridge | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { __lazysemConsent?: ConsentBridge })
    .__lazysemConsent;
}

/** Read the persisted choice. Returns null when no decision has been made
 *  yet (the banner uses this as the trigger to render). */
export function readConsent(): PersistedConsent | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedConsent>;
    // Defensive: missing version → treat as stale and re-prompt.
    if (parsed.version !== 1) return null;
    return {
      analytics_storage: parsed.analytics_storage ?? "denied",
      ad_storage: parsed.ad_storage ?? "denied",
      ad_user_data: parsed.ad_user_data ?? "denied",
      ad_personalization: parsed.ad_personalization ?? "denied",
      updated_at: parsed.updated_at ?? new Date(0).toISOString(),
      version: 1,
    };
  } catch (err) {
    logger.warn(
      "[consent] failed to parse stored consent, will re-prompt",
      err,
    );
    return null;
  }
}

/** Persist the user's choice and forward it to the GTM bridge. Idempotent.
 *  Returns the payload that was written so callers can update their state
 *  from the same source of truth without re-reading localStorage. */
export function writeConsent(choice: ConsentChoice): PersistedConsent {
  const payload: PersistedConsent = {
    ...choice,
    updated_at: new Date().toISOString(),
    version: 1,
  };
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(payload));
    } catch (err) {
      // localStorage can throw in private mode / when quota is exceeded.
      // We still push to GTM below so the in-page session honors the choice.
      logger.warn("[consent] failed to persist consent", err);
    }
  }
  const bridge = getBridge();
  if (bridge?.update) {
    bridge.update(choice);
  }
  return payload;
}

/** Clear the persisted choice. Used by the "withdraw consent" path; the
 *  banner will re-render on next mount. Also tells GTM to revert every
 *  category back to denied so the rest of the page stops collecting. */
export function clearConsent(): void {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.removeItem(CONSENT_STORAGE_KEY);
    } catch (err) {
      logger.warn("[consent] failed to clear stored consent", err);
    }
  }
  const bridge = getBridge();
  if (bridge?.update) bridge.update(ALL_DENIED);
}
